import React, { Component, ErrorInfo, ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Home, RefreshCw, ArrowLeft, Copy, Check, ChevronDown, ChevronRight, Bug, MessageCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '@/components/Footer';
import { collectErrorInfo, formatErrorReport, copyToClipboard } from '@/lib/error-utils';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

// 함수형 컴포넌트로 래핑하여 훅 사용
const ErrorFallback: React.FC<{
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  eventId?: string;
}> = ({ error, errorInfo, onReset, eventId }) => {
  const navigate = useNavigate();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // 사용자 피드백 상태
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // 에러 리포트 복사 핸들러
  const handleCopyErrorReport = async () => {
    if (!error) return;
    
    const errorReport = collectErrorInfo(error, errorInfo || undefined);
    const formattedReport = formatErrorReport(errorReport);
    
    const success = await copyToClipboard(formattedReport);
    
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000); // 3초 후 원래 상태로
    } else {
      alert('복사에 실패했습니다. 수동으로 텍스트를 선택해서 복사해주세요.');
    }
  };

  // 사용자 피드백 제출 핸들러
  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setFeedbackSubmitting(true);

    try {
      // Sentry User Feedback API 사용 - 대시보드에서 User Feedback으로 표시됨
      if (eventId) {
        // @sentry/core에서 captureUserFeedback 가져오기
        const { captureUserFeedback } = await import('@sentry/core');
        captureUserFeedback({
          event_id: eventId,
          name: '사용자', // 익명 사용자
          email: 'anonymous@wholsee.kr', // 익명 이메일
          comments: feedbackText
        });
      } else {
        // eventId가 없는 경우 일반 메시지로 전송
        Sentry.captureMessage(`사용자 피드백: ${feedbackText}`, {
          level: 'info',
          tags: {
            source: 'error_boundary',
            type: 'user_feedback'
          },
          contexts: {
            feedback: {
              user_input: feedbackText,
              timestamp: new Date().toISOString(),
              error_message: error?.message
            }
          }
        });
      }

      setFeedbackSubmitted(true);
    } catch (err) {
      console.error("피드백 제출 실패:", err);
      alert('피드백 전송에 실패했습니다. 고객센터로 직접 연락해주세요.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header 제거 - AuthProvider가 아직 설정되지 않은 상태 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="flex items-center">
            <img src="/images/logo.svg" alt="홀씨" className="h-8 w-auto" />
          </Link>
        </div>
      </div>
      
      <main className="flex-1 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            {/* 일러스트 영역 */}
            <div className="mb-8 relative">
              {/* 산 일러스트 - SVG로 직접 그리기 */}
              <svg 
                viewBox="0 0 400 300" 
                className="w-full max-w-md mx-auto"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* 배경 하늘 */}
                <rect width="400" height="200" fill="#E0E7FF" opacity="0.3"/>
                
                {/* 산 실루엣 */}
                <path 
                  d="M0 200 L100 100 L200 150 L300 50 L400 150 L400 300 L0 300 Z" 
                  fill="#9CA3AF"
                  opacity="0.3"
                />
                <path 
                  d="M0 250 L150 120 L250 180 L400 100 L400 300 L0 300 Z" 
                  fill="#6B7280"
                  opacity="0.4"
                />
                
                {/* 길 잃은 등산객 아이콘 */}
                <g transform="translate(180, 160)">
                  <circle cx="20" cy="20" r="35" fill="#FEE2E2" />
                  <text 
                    x="20" 
                    y="30" 
                    fontSize="30" 
                    textAnchor="middle"
                    className="select-none"
                  >
                    🧭
                  </text>
                </g>
                
                {/* 물음표들 */}
                <text x="100" y="80" fontSize="20" opacity="0.5">❓</text>
                <text x="300" y="120" fontSize="20" opacity="0.5">❓</text>
                <text x="250" y="200" fontSize="20" opacity="0.5">❓</text>
              </svg>
              
              {/* 경고 배지 */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4">
                <div className="bg-red-100 rounded-full p-3 shadow-lg">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>
            </div>

            {/* 메시지 영역 */}
            <div className="space-y-4 mb-8">
              <h1 className="text-3xl font-bold text-gray-900">
                앗, 길을 잃으셨나요?
              </h1>
              <p className="text-lg text-gray-600">
                등산로에서 잠시 벗어나신 것 같습니다.
              </p>
              <p className="text-gray-500">
                걱정 마세요! 안전하게 원래 길로 돌아갈 수 있도록 도와드릴게요.
              </p>
            </div>

            {/* 액션 버튼들 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
              <Button
                onClick={onReset}
                variant="default"
                className="flex items-center justify-center gap-2 h-12"
              >
                <RefreshCw className="h-4 w-4" />
                다시 시도
              </Button>
              
              <Button
                onClick={handleGoBack}
                variant="outline"
                className="flex items-center justify-center gap-2 h-12"
              >
                <ArrowLeft className="h-4 w-4" />
                이전 페이지
              </Button>
              
              <Link to="/">
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 h-12 w-full"
                >
                  <Home className="h-4 w-4" />
                  홈으로
                </Button>
              </Link>
            </div>

            {/* 추가 도움말 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-lg mx-auto mb-6">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                이런 방법도 있어요
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• 브라우저를 새로고침 해보세요 (Ctrl/Cmd + R)</li>
                <li>• 잠시 후 다시 시도해보세요</li>
                <li>• 문제가 계속되면 고객센터로 연락주세요</li>
              </ul>
            </div>

            {/* 사용자 피드백 섹션 */}
            {!feedbackSubmitted && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left max-w-lg mx-auto mb-6">
                <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  무엇을 하려고 하셨나요?
                </h3>
                <p className="text-sm text-green-800 mb-3">
                  오류가 발생하기 전에 하려던 작업을 알려주시면 문제 해결에 도움이 됩니다.
                </p>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="예: '투어 예약을 하려고 했는데...' 또는 '결제 버튼을 클릭했을 때...'"
                  className="w-full p-3 border border-green-300 rounded-lg resize-none h-24 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  disabled={feedbackSubmitting}
                />
                <Button 
                  onClick={handleSubmitFeedback}
                  disabled={!feedbackText.trim() || feedbackSubmitting}
                  className="w-full mt-3 bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  {feedbackSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                      전송 중...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      피드백 보내기
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* 피드백 제출 완료 메시지 */}
            {feedbackSubmitted && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center max-w-lg mx-auto mb-6">
                <div className="flex items-center justify-center gap-2 text-green-700 mb-2">
                  <Check className="h-5 w-5" />
                  <span className="font-semibold">피드백을 보내주셔서 감사합니다!</span>
                </div>
                <p className="text-sm text-green-600">
                  빠른 시일 내에 문제를 해결하겠습니다.
                </p>
              </div>
            )}

            {/* 상세 에러 정보 섹션 - 모든 환경에서 표시 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left max-w-2xl mx-auto">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsDetailOpen(!isDetailOpen)}
                >
                  <h3 className="font-semibold text-red-900 flex items-center gap-2">
                    <Bug className="h-5 w-5" />
                    📋 에러 상세 정보
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyErrorReport();
                      }}
                      variant="outline"
                      size="sm"
                      className={`text-xs ${copySuccess ? 'bg-green-100 text-green-700' : 'bg-white text-red-700'} border-red-300 hover:bg-red-100`}
                    >
                      {copySuccess ? (
                        <>
                          <Check className="h-3 w-3 mr-1" />
                          복사됨!
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3 mr-1" />
                          복사
                        </>
                      )}
                    </Button>
                    {isDetailOpen ? (
                      <ChevronDown className="h-4 w-4 text-red-600" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </div>
                
                <div className="mt-3 text-sm text-red-800">
                  <p className="mb-2">
                    이 정보를 복사해서 고객센터로 전달해주시면 빠른 문제 해결이 가능합니다.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-red-100 px-2 py-1 rounded">📧 support@wholsee.kr</span>
                    <span className="bg-red-100 px-2 py-1 rounded">📞 010-3741-2556</span>
                  </div>
                </div>

                {isDetailOpen && (
                  <div className="mt-4 space-y-4">
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                      <div className="text-xs text-gray-400 mb-2">기본 에러 정보</div>
                      <p className="text-red-400 font-mono text-sm mb-2">
                        {error.name}: {error.message}
                      </p>
                      <div className="text-xs text-gray-400 mb-1">발생 시각: {new Date().toLocaleString('ko-KR')}</div>
                      <div className="text-xs text-gray-400 mb-1">페이지: {window.location.href}</div>
                      <div className="text-xs text-gray-400">브라우저: {navigator.userAgent.split(' ').slice(-2).join(' ')}</div>
                    </div>

                    {error.stack && (
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        <div className="text-xs text-gray-400 mb-2">스택 트레이스</div>
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {error.stack}
                        </pre>
                      </div>
                    )}

                    {errorInfo && errorInfo.componentStack && (
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        <div className="text-xs text-gray-400 mb-2">컴포넌트 스택</div>
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}

                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                      <div className="text-xs text-yellow-800">
                        💡 <strong>참고:</strong> 위의 "복사" 버튼을 누르면 모든 정보가 정리된 형태로 복사됩니다.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

class ImprovedErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    eventId: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null, eventId: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 ImprovedErrorBoundary가 에러를 캐치했습니다:', error);
    console.error('에러 정보:', errorInfo);
    
    // Sentry에 에러 보고 및 eventId 받기
    const eventId = Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', 'ImprovedErrorBoundary');
      scope.setLevel('error');
      
      // 컴포넌트 스택 정보 추가
      if (errorInfo?.componentStack) {
        scope.setContext('componentStack', {
          componentStack: errorInfo.componentStack,
        });
      }
      
      // 브라우저 정보 추가
      scope.setContext('browser', {
        userAgent: navigator.userAgent,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      });
      
      // URL 정보 추가
      scope.setContext('page', {
        url: window.location.href,
        referrer: document.referrer,
        title: document.title,
      });
      
      return Sentry.captureException(error);
    });

    this.setState({
      error,
      errorInfo,
      eventId
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
    // 페이지 새로고침으로 완전히 초기화
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          eventId={this.state.eventId}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ImprovedErrorBoundary;